(() => {
  const revealed = new WeakSet();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const railTimers = new WeakMap();
  const revealObserver = 'IntersectionObserver' in window && !reduceMotion.matches
    ? new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('krt-reveal-pending');
          entry.target.getBoundingClientRect();
          requestAnimationFrame(() => entry.target.classList.add('is-visible'));
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
    : null;
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const open = [...document.querySelectorAll('[data-krt-menu][open]')];
    if (!open.length) return;
    const focused = open.find(el => el.contains(document.activeElement));
    open.forEach(el => { el.open = false; });
    (focused || open[0]).querySelector('summary')?.focus();
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('[data-krt-menu][open]').forEach(el => {
      if (!el.contains(event.target)) el.open = false;
    });
  });
  document.addEventListener('toggle', event => {
    if (!(event.target instanceof HTMLDetailsElement) || !event.target.matches('[data-krt-menu]') || !event.target.open) return;
    document.querySelectorAll('[data-krt-menu][open]').forEach(el => {
      if (el !== event.target && !el.contains(event.target)) el.open = false;
    });
  }, true);
  document.addEventListener('open', event => {
    if (!(event.target instanceof HTMLElement) || !event.target.matches('shopify-account')) return;
    event.target.style.setProperty('--account-dialog-top', `${Math.max(0,Math.round(event.target.getBoundingClientRect().bottom))}px`);
  }, true);
  const railContainer = element => element.closest('.krt-section, .krt-page-map');
  const railProgress = rail => {
    let progress = rail.nextElementSibling;
    if (progress?.matches('[data-krt-rail-progress]')) return progress;
    progress = document.createElement('div');
    progress.className = 'krt-rail-progress';
    progress.dataset.krtRailProgress = '';
    progress.setAttribute('aria-hidden', 'true');
    progress.append(document.createElement('span'));
    rail.insertAdjacentElement('afterend', progress);
    return progress;
  };
  const updateRailControls = rail => {
    const section = railContainer(rail);
    if (!section) return;
    const previous = section.querySelector('[data-krt-rail-previous]');
    const next = section.querySelector('[data-krt-rail-next]');
    const progress = railProgress(rail);
    const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const offset = Math.min(maximum, Math.max(0, rail.scrollLeft));
    const thumbSize = rail.scrollWidth > 0 ? Math.min(1, rail.clientWidth / rail.scrollWidth) : 1;
    const thumbStart = maximum > 0 ? (offset / maximum) * (1 - thumbSize) : 0;
    if (previous) previous.disabled = offset <= 2;
    if (next) next.disabled = offset >= maximum - 2;
    progress.hidden = maximum <= 2;
    progress.style.setProperty('--krt-rail-thumb-size', `${thumbSize * 100}%`);
    progress.style.setProperty('--krt-rail-thumb-start', `${thumbStart * 100}%`);
  };
  const moveRail = (rail, direction) => {
    rail.scrollBy({ left: direction * Math.max(280, rail.clientWidth * 0.82), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  };
  const stopRailAutoplay = rail => {
    const timer = railTimers.get(rail);
    if (timer) window.clearTimeout(timer);
    railTimers.delete(rail);
  };
  const syncRailAutoplayToggle = rail => {
    const toggle = railContainer(rail)?.querySelector('[data-krt-rail-toggle]');
    if (!toggle) return;
    const paused = rail.dataset.krtRailUserPaused === 'true' || reduceMotion.matches;
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
    const symbol = toggle.querySelector('[aria-hidden="true"]');
    if (symbol) symbol.textContent = paused ? '▶' : 'Ⅱ';
    toggle.disabled = reduceMotion.matches;
  };
  const railCanAutoplay = rail => (
    rail.hasAttribute('data-krt-rail-autoplay') &&
    rail.dataset.krtRailUserPaused !== 'true' &&
    rail.dataset.krtRailInteractionPaused !== 'true' &&
    !reduceMotion.matches &&
    !document.hidden &&
    rail.scrollWidth - rail.clientWidth > 2
  );
  const scheduleRailAutoplay = rail => {
    stopRailAutoplay(rail);
    syncRailAutoplayToggle(rail);
    if (!railCanAutoplay(rail)) return;
    const requested = Number.parseInt(rail.dataset.krtRailInterval || '4000', 10);
    const interval = Math.min(12000, Math.max(2000, Number.isFinite(requested) ? requested : 4000));
    railTimers.set(rail, window.setTimeout(() => {
      if (!railCanAutoplay(rail)) return;
      const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      if (rail.scrollLeft >= maximum - 2) {
        rail.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        moveRail(rail, 1);
      }
      scheduleRailAutoplay(rail);
    }, interval));
  };
  const setRailInteractionPause = (rail, paused) => {
    rail.dataset.krtRailInteractionPaused = String(paused);
    if (paused) stopRailAutoplay(rail); else scheduleRailAutoplay(rail);
  };
  const initialiseRail = rail => {
    if (rail.dataset.krtRailReady === 'true') return;
    rail.dataset.krtRailReady = 'true';
    const section = railContainer(rail);
    rail.addEventListener('scroll', () => updateRailControls(rail), { passive: true });
    rail.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      moveRail(rail, event.key === 'ArrowLeft' ? -1 : 1);
    });
    section?.addEventListener('pointerenter', () => setRailInteractionPause(rail, true));
    section?.addEventListener('pointerleave', () => setRailInteractionPause(rail, false));
    section?.addEventListener('focusin', () => setRailInteractionPause(rail, true));
    section?.addEventListener('focusout', event => {
      if (!section.contains(event.relatedTarget)) setRailInteractionPause(rail, false);
    });
    updateRailControls(rail);
    scheduleRailAutoplay(rail);
  };
  const businessApplicationServices = new Set(['business-applications', 'business-central', 'shopify-business-central']);
  const updateBusinessApplicationInterests = select => {
    const fieldset = select.form?.querySelector('[data-krt-business-applications-interests]');
    if (!fieldset) return;
    const visible = businessApplicationServices.has(select.value);
    fieldset.hidden = !visible;
    fieldset.querySelectorAll('input').forEach(input => {
      input.disabled = !visible;
      if (!visible) input.checked = false;
    });
  };
  const initialise = () => {
    const service = new URLSearchParams(location.search).get('service');
    document.querySelectorAll('[data-krt-service]').forEach(select => {
      if (service && [...select.options].some(option => option.value === service)) select.value = service;
      updateBusinessApplicationInterests(select);
    });
    document.body.classList.add('krt-reveal-ready');
    document.querySelectorAll('[data-krt-motion="cascade"]').forEach(section => {
      section.querySelectorAll('[data-krt-reveal-mode="cascade"]').forEach((element, index) => {
        element.style.setProperty('--krt-reveal-order', String(Math.min(index, 8)));
      });
    });
    document.querySelectorAll('[data-krt-reveal]').forEach(element => {
      if (revealed.has(element)) return;
      revealed.add(element);
      if (revealObserver) revealObserver.observe(element);
      else element.classList.add('is-visible');
    });
    document.querySelectorAll('[data-krt-rail]').forEach(initialiseRail);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise); else initialise();
  document.addEventListener('shopify:section:load', initialise);
  document.addEventListener('change', event => {
    if (event.target.matches('[data-krt-service]')) updateBusinessApplicationInterests(event.target);
    if (event.target.matches('[data-krt-resource-choices] input[type="checkbox"]')) {
      event.target.closest('[data-krt-resource-choices]').querySelectorAll('input[type="checkbox"]').forEach(input => input.setCustomValidity(''));
    }
  });
  document.addEventListener('submit', event => {
    const choices = event.target.querySelector?.('[data-krt-resource-choices]');
    if (!choices) return;
    const inputs = [...choices.querySelectorAll('input[type="checkbox"]')];
    if (inputs.some(input => input.checked)) return;
    event.preventDefault();
    inputs[0].setCustomValidity('Choose at least one guide.');
    inputs[0].reportValidity();
    inputs[0].focus();
  });
  document.addEventListener('input', event => {
    if (!event.target.matches('[data-krt-filter]')) return;
    const value = event.target.value.toLocaleLowerCase().trim();
    const cards = [...document.querySelectorAll('[data-krt-searchable]')];
    cards.forEach(card => { card.hidden = !card.textContent.toLocaleLowerCase().includes(value); });
    document.querySelectorAll('[data-krt-directory-group]').forEach(group => {
      const hasVisible = Boolean(group.querySelector('[data-krt-searchable]:not([hidden])'));
      group.hidden = value !== '' && !hasVisible;
      if (!group.matches('details')) return;
      if (value !== '') {
        if (group.dataset.krtFilterOpen === undefined) group.dataset.krtFilterOpen = String(group.open);
        group.open = hasVisible;
      } else if (group.dataset.krtFilterOpen !== undefined) {
        group.open = group.dataset.krtFilterOpen === 'true';
        delete group.dataset.krtFilterOpen;
      }
    });
    const status = document.querySelector('[data-krt-filter-status]');
    if (status) status.textContent = `${cards.filter(card => !card.hidden).length} ${status.dataset.suffix || ''}`;
    document.querySelectorAll('[data-krt-rail]').forEach(updateRailControls);
  });
  document.addEventListener('click', event => {
    const toggle = event.target.closest('[data-krt-rail-toggle]');
    if (toggle) {
      const rail = railContainer(toggle)?.querySelector('[data-krt-rail]');
      if (!rail) return;
      rail.dataset.krtRailUserPaused = String(rail.dataset.krtRailUserPaused !== 'true');
      scheduleRailAutoplay(rail);
      return;
    }
    const button = event.target.closest('[data-krt-rail-previous],[data-krt-rail-next]');
    if (!button) return;
    const rail = railContainer(button)?.querySelector('[data-krt-rail]');
    if (!rail) return;
    const direction = button.matches('[data-krt-rail-previous]') ? -1 : 1;
    moveRail(rail, direction);
  });
  const refreshRailAutoplay = () => document.querySelectorAll('[data-krt-rail]').forEach(scheduleRailAutoplay);
  document.addEventListener('visibilitychange', refreshRailAutoplay);
  reduceMotion.addEventListener?.('change', refreshRailAutoplay);
  const activateHotspot = marker => {
    const hotspot = marker.closest('[data-krt-hotspot]');
    if (!hotspot) return;
    const index = marker.dataset.krtHotspotIndex;
    hotspot.querySelectorAll('[data-krt-hotspot-marker]').forEach(item => item.setAttribute('aria-current', String(item === marker)));
    hotspot.querySelectorAll('[data-krt-hotspot-panel]').forEach(panel => panel.classList.toggle('is-active', panel.dataset.krtHotspotIndex === index));
  };
  document.addEventListener('click', event => {
    const marker = event.target.closest('[data-krt-hotspot-marker]');
    if (marker) activateHotspot(marker);
  });
  document.addEventListener('focusin', event => {
    const marker = event.target.closest('[data-krt-hotspot-marker]');
    if (marker) activateHotspot(marker);
  });
  window.addEventListener('resize', () => document.querySelectorAll('[data-krt-rail]').forEach(rail => {
    updateRailControls(rail);
    scheduleRailAutoplay(rail);
  }));
})();
